import { useMemo, useState } from 'react'

import { SegmentedControl } from '@mantine/core'
import dayjs from 'dayjs'
import { Metadata, useQuery } from '@redwoodjs/web'

import { VoEType } from 'src/graphql/types/verificationOfEmployments'

import {
  GET_VOE_HERO_STATS,
  GET_VOE_COMPLETIONS_BY_CHANNEL,
  GET_VOE_RESPONSE_RATE,
  GET_VOE_TIME_TO_FIRST_CONTACT,
  GET_VOE_LIFECYCLE_BREAKDOWN,
  GET_VOE_AUDIT_TRAIL_ACTIVITY,
  GET_VOE_OUTREACH_SUMMARY,
  GET_VOE_AVG_OUTREACH_ATTEMPTS,
  GET_VOE_AUTONOMY_SPLIT,
} from 'src/graphql/voeAnalytics.graphql'

import { VoEAnalyticsCard } from './components/VoEAnalyticsCard'
import { VoEAuditTrailActivityChart } from './components/VoEAuditTrailActivityChart'
import { VoEAutonomySplitChart } from './components/VoEAutonomySplitChart'
import { VoEAvgAttemptsChart } from './components/VoEAvgAttemptsChart'
import { VoEBarChart } from './components/VoEBarChart'
import {
  VoEFiltersBar,
  type VoEAnalyticsFilters,
} from './components/VoEFiltersBar'
import { VoEHeroStatCard } from './components/VoEHeroStatCard'
import { VoELifecycleChart } from './components/VoELifecycleChart'
import { VoELineChart } from './components/VoELineChart'
import { VoEOutreachSummaryChart } from './components/VoEOutreachSummaryChart'
import { VoEStackedBarChart } from './components/VoEStackedBarChart'

const EMPTY_HERO = {
  totalCompletions: 0,
  dnFormCount: 0,
  dnFormPercentage: 0,
  responseReceivedRate: 0,
  avgDaysToFirstContact: null,
  avgTotalLifecycleDays: null,
}

const round1 = (n: number | null | undefined): number =>
  Math.round((n ?? 0) * 10) / 10

const DEFAULT_START = dayjs().subtract(1, 'month').startOf('day').toDate()
const DEFAULT_END = new Date()
const DEFAULT_FILTERS: VoEAnalyticsFilters = {
  voeTypes: [VoEType.OUTBOUND],
  dateRange: {
    gte: DEFAULT_START.toISOString(),
    lte: DEFAULT_END.toISOString(),
  },
}

const VerificationAnalyticsPage = () => {
  const [filters, setFilters] = useState<VoEAnalyticsFilters>(DEFAULT_FILTERS)
  const [startDate, setStartDate] = useState<Date | null>(DEFAULT_START)
  const [endDate, setEndDate] = useState<Date | null>(DEFAULT_END)
  const [channelGranularity, setChannelGranularity] = useState('week')
  const [outreachGranularity, setOutreachGranularity] = useState('week')
  const [autonomyGranularity, setAutonomyGranularity] = useState('week')

  const channelFilters = useMemo(
    () => ({ ...filters, granularity: channelGranularity }),
    [filters, channelGranularity]
  )
  const outreachFilters = useMemo(
    () => ({ ...filters, granularity: outreachGranularity }),
    [filters, outreachGranularity]
  )

  const autonomyFilters = useMemo(
    () => ({ ...filters, granularity: autonomyGranularity }),
    [filters, autonomyGranularity]
  )

  const { data: heroData, loading: heroLoading } = useQuery(
    GET_VOE_HERO_STATS,
    { variables: { filters } }
  )

  const { data: channelData, loading: channelLoading } = useQuery(
    GET_VOE_COMPLETIONS_BY_CHANNEL,
    { variables: { filters: channelFilters }, fetchPolicy: 'cache-and-network' }
  )

  const { data: responseData, loading: responseLoading } = useQuery(
    GET_VOE_RESPONSE_RATE,
    { variables: { filters } }
  )

  const { data: ttfcData, loading: ttfcLoading } = useQuery(
    GET_VOE_TIME_TO_FIRST_CONTACT,
    { variables: { filters } }
  )

  const { data: lifecycleData, loading: lifecycleLoading } = useQuery(
    GET_VOE_LIFECYCLE_BREAKDOWN,
    { variables: { filters } }
  )

  const { data: auditTrailData, loading: auditTrailLoading } = useQuery(
    GET_VOE_AUDIT_TRAIL_ACTIVITY,
    { variables: { filters: outreachFilters }, fetchPolicy: 'cache-and-network' }
  )

  const { data: outreachSummaryData, loading: outreachSummaryLoading } =
    useQuery(GET_VOE_OUTREACH_SUMMARY, { variables: { filters } })

  const { data: avgAttemptsData, loading: avgAttemptsLoading } = useQuery(
    GET_VOE_AVG_OUTREACH_ATTEMPTS,
    { variables: { filters } }
  )

  const { data: autonomyData, loading: autonomyLoading } = useQuery(
    GET_VOE_AUTONOMY_SPLIT,
    {
      variables: { filters: autonomyFilters },
      fetchPolicy: 'cache-and-network',
    }
  )

  const stats = heroData?.voeHeroStats ?? EMPTY_HERO
  const auditTrailBuckets = auditTrailData?.voeAuditTrailActivity?.buckets ?? []
  const autonomySplit = autonomyData?.voeAutonomySplit ?? null
  const outreachSummary = outreachSummaryData?.voeOutreachSummary ?? null
  const avgAttempts = avgAttemptsData?.voeAvgOutreachAttempts ?? null
  const completionsByChannel = channelData?.voeCompletionsByChannel ?? []
  const responseRate = responseData?.voeResponseRate ?? []
  const timeToFirstContact = ttfcData?.voeTimeToFirstContact ?? []
  const lifecycle = lifecycleData?.voeLifecycleBreakdown ?? null

  return (
    <div className="flex h-full flex-col gap-6 overflow-auto px-5 pb-5">
      <Metadata
        title="Verification Analytics"
        description="Verification of Employment Analytics"
      />

      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-semibold text-doubleNickel-gray-800">
            Verification Analytics
          </div>
          <p className="mt-0.5 text-sm text-gray-500">
            Employment verification completion tracking
          </p>
        </div>
        <VoEFiltersBar
          startDate={startDate}
          endDate={endDate}
          onFiltersChange={setFilters}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
      </div>

      {/* Hero stats row */}
      <div className="grid grid-cols-5 gap-4">
        <VoEHeroStatCard
          label="Total Completions"
          value={stats.totalCompletions}
          info="Total number of outbound employment verifications marked as completed in the selected date range."
          loading={heroLoading}
        />
        <VoEHeroStatCard
          label="Double Nickel %"
          value={round1(stats.dnFormPercentage)}
          suffix="%"
          info="Percentage of completions where the employer responded via the Double Nickel form."
          loading={heroLoading}
        />
        <VoEHeroStatCard
          label="Response Rate"
          value={round1(stats.responseReceivedRate)}
          suffix="%"
          info="Percentage of completions where the employer actually responded (excludes No Response and Unknown)."
          loading={heroLoading}
        />
        <VoEHeroStatCard
          label="Avg Days to Contact"
          value={round1(stats.avgDaysToFirstContact)}
          suffix="d"
          info="Average number of days from when a verification was created to when the first outreach attempt was made. Only includes verifications where the employer permitted contact."
          loading={heroLoading}
        />
        <VoEHeroStatCard
          label="Avg Lifecycle"
          value={round1(stats.avgTotalLifecycleDays)}
          suffix="d"
          info="Average number of days from creation to completion across all completed verifications where the employer permitted contact."
          loading={heroLoading}
        />
      </div>

      {/* Verification response attribution — full width */}
      <VoEAnalyticsCard
        title="Verification Response Attribution"
        subtitle="Answered verifications by automated, assisted and manual"
        info="Each verification is counted once, when the employer's answer first arrived, whether or not it has been marked completed. Answers with no logged outreach are counted as manual."
        loading={autonomyLoading}
        headerRight={
          <SegmentedControl
            size="xs"
            value={autonomyGranularity}
            onChange={setAutonomyGranularity}
            data={[
              { label: 'Weekly', value: 'week' },
              { label: 'Monthly', value: 'month' },
            ]}
          />
        }
      >
        <VoEAutonomySplitChart data={autonomySplit} />
      </VoEAnalyticsCard>

      {/* Completions by channel - full width */}
      <VoEAnalyticsCard
        title="Completions by Channel"
        subtitle="Breakdown by completion method"
        info="Breakdown of how each employment verification was completed — Double Nickel form, phone, fax, email, ATS platform, or no response."
        loading={channelLoading}
        headerRight={
          <SegmentedControl
            size="xs"
            value={channelGranularity}
            onChange={setChannelGranularity}
            data={[
              { label: 'Daily', value: 'day' },
              { label: 'Weekly', value: 'week' },
              { label: 'Monthly', value: 'month' },
            ]}
          />
        }
      >
        <VoEStackedBarChart data={completionsByChannel} />
      </VoEAnalyticsCard>

      {/* Three-col: Response rate + Time to contact + Lifecycle */}
      <div className="grid grid-cols-3 gap-6">
        <VoEAnalyticsCard
          title="Response Received Rate"
          subtitle="Percentage of verifications with employer response"
          info="Percentage of completed verifications where the employer actually responded (excludes No Response and Unknown)."
          loading={responseLoading}
        >
          <VoELineChart data={responseRate} />
        </VoEAnalyticsCard>
        <VoEAnalyticsCard
          title="Time to First Contact"
          subtitle="Average days from creation to first outreach"
          info="Average number of days from when a verification was created to when the first outreach attempt was made. Only includes verifications where the employer permitted contact."
          loading={ttfcLoading}
        >
          <VoEBarChart data={timeToFirstContact} />
        </VoEAnalyticsCard>
        <VoEAnalyticsCard
          title="Verification Lifecycle"
          subtitle="Average time in each phase"
          info="Average and median time spent in each phase: Wait (created → first contact), Active (first contact → completion), and Total (created → completion). Only includes verifications where the employer permitted contact."
          loading={lifecycleLoading}
        >
          <VoELifecycleChart data={lifecycle} />
        </VoEAnalyticsCard>
      </div>

      {/* Outreach row: activity + attempts + breakdown (equal thirds) */}
      <div className="grid grid-cols-3 gap-6">
        <VoEAnalyticsCard
          title="Outreach Activity"
          subtitle="Attempts by type over time"
          info="Number of outreach attempts (email, call, fax, ATS) logged across all outbound verifications, regardless of completion status."
          loading={auditTrailLoading}
          headerRight={
            <SegmentedControl
              size="xs"
              value={outreachGranularity}
              onChange={setOutreachGranularity}
              data={[
                { label: 'Weekly', value: 'week' },
                { label: 'Monthly', value: 'month' },
              ]}
            />
          }
        >
          <VoEAuditTrailActivityChart data={auditTrailBuckets} />
        </VoEAnalyticsCard>
        <VoEAnalyticsCard
          title="Outreach Attempts"
          subtitle="Average per verification by outcome"
          info="Average number of outreach attempts (call, email, fax, ATS) per verification, split by completion outcome. 'Completed' includes verifications where the employer responded. 'No Response' counts those marked with no employer reply."
          loading={avgAttemptsLoading}
        >
          <VoEAvgAttemptsChart data={avgAttempts} />
        </VoEAnalyticsCard>
        <VoEAnalyticsCard
          title="Outreach Breakdown"
          subtitle="Automated, assisted and manual"
          info="Automated attempts are system-scheduled emails and voice-agent calls. Assisted attempts are voice-agent calls a team member launched. Manual attempts are logged directly by team members."
          loading={outreachSummaryLoading}
        >
          <VoEOutreachSummaryChart data={outreachSummary} />
        </VoEAnalyticsCard>
      </div>

    </div>
  )
}

export default VerificationAnalyticsPage
